import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import PopulationFilterBar from './PopulationFilterBar';
import { populationRecordsApi } from '../../../api/populationRecordsApi';
import { loadPopulationRefData } from '../../../api/populationRefApi';
import { useAuth } from '../../../context/AuthContext';
import { useSync } from '../../../context/SyncContext';
import { useNotifications } from '../../../context/NotificationContext';
import { useCollectionSync } from '../../../hooks/useCollectionSync';
import { anchorRect, useAnchoredMenu } from '../../shared/useAnchoredMenu';

// ── Status badge helper ───────────────────────────────────────────────────────
const STATUS_TONES = {
  draft:     'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
  submitted: 'background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;',
  validated: 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;',
  approved:  'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  rejected:  'background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;',
};

function statusBadge(value, type) {
  if (type !== 'display') return value ?? '';
  if (!value) return '—';
  const key = String(value).toLowerCase();
  const style = STATUS_TONES[key] ?? STATUS_TONES.draft;
  return `<span style="display:inline-flex;align-items:center;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;${style}">${value.charAt(0).toUpperCase() + value.slice(1)}</span>`;
}

function numCell(value, type) {
  if (type !== 'display') return value ?? 0;
  return value == null ? '—' : Number(value).toLocaleString();
}

function textCell(value, type) {
  if (type !== 'display') return value ?? '';
  return value || '—';
}

const ACT = (_d, type) =>
  type !== 'display' ? '' :
  `<button class="nlims-action-btn" data-action="menu"
    style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:4px 14px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;" title="Actions">&#8943;</button>`;

const POPULATION_COLUMNS = [
  { title: 'State',              data: 'state',             sortable: true,  render: textCell  },
  { title: 'LGA',                data: 'lga',               sortable: true,  render: textCell  },
  { title: 'Period',             data: 'period',            sortable: true,  render: textCell  },
  { title: 'Species',            data: 'species',           sortable: true,  render: textCell  },
  { title: 'Prod. System',       data: 'productionSystem',  sortable: true,  render: textCell  },
  { title: 'Prod. Purpose',      data: 'productionPurpose', sortable: true,  render: textCell  },
  { title: 'Population',         data: 'populationCount',   sortable: true,  render: numCell   },
  { title: 'Sex',                data: 'sex',               sortable: true,  render: textCell  },
  { title: 'Age Class',          data: 'ageClass',          sortable: true,  render: textCell, defaultContent: '' },
  { title: 'Herds/Flocks',       data: 'numberHerdsFlocks', sortable: true,  render: numCell   },
  { title: 'Offtake',            data: 'periodOfftake',     sortable: true,  render: numCell, defaultContent: '' },
  { title: 'Count Method',       data: 'countMethod',       sortable: true,  render: textCell  },
  { title: 'Data Source',        data: 'dataSource',        sortable: true,  render: textCell  },
  { title: 'Confidence',         data: 'confidenceLevel',   sortable: true,  render: textCell, defaultContent: '' },
  { title: 'Status',             data: 'validationStatus',  sortable: true,  render: statusBadge },
  { title: 'Action',             data: null,                sortable: false, render: ACT       },
];

// Fetch a single, large, unfiltered batch — the dropdown filters below are
// applied client-side (see filterPopulationRows), the same guaranteed-works
// approach Conflicts & Dispute Records uses, since the backend doesn't
// reliably honor these query params.
function toApiParams() {
  return { per_page: 1000 };
}

function normalizeForCompare(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Client-side filtering — mirrors the dropdown filter set from
// PopulationFilterBar.jsx, matching against whichever field on the
// normalized row actually holds a comparable value (raw code where one
// exists, otherwise the humanized display text via normalizeForCompare so
// case/spacing/underscore differences don't cause false negatives).
function filterPopulationRows(rows, filters) {
  const state = normalizeForCompare(filters.state);
  const species = normalizeForCompare(filters.species_uuid ?? filters.species);
  const productionSystem = normalizeForCompare(filters.productionSystem);
  const countMethod = normalizeForCompare(filters.countMethod);
  const validationStatus = normalizeForCompare(filters.validationStatus);
  const period = normalizeForCompare(filters.period);
  const sex = normalizeForCompare(filters.sex);

  return rows.filter((row) => {
    if (state && normalizeForCompare(row.state) !== state) return false;
    if (species) {
      const rowSpecies = normalizeForCompare(row.species);
      const rowSpeciesUuid = normalizeForCompare(row.speciesUuid);
      if (rowSpecies !== species && rowSpeciesUuid !== species) return false;
    }
    if (productionSystem && normalizeForCompare(row.productionSystem) !== productionSystem) return false;
    if (countMethod) {
      const rowCountMethod = normalizeForCompare(row.countMethod);
      const rowCountMethodCode = normalizeForCompare(row.countMethodCode);
      if (rowCountMethod !== countMethod && rowCountMethodCode !== countMethod) return false;
    }
    if (validationStatus) {
      const rowStatus = normalizeForCompare(row.validationStatus);
      const rowDataStatus = normalizeForCompare(row.data_status);
      if (rowStatus !== validationStatus && rowDataStatus !== validationStatus) return false;
    }
    if (period && normalizeForCompare(row.period) !== period) return false;
    if (sex && normalizeForCompare(row.sex) !== sex) return false;
    return true;
  });
}

function enrichRow(r, specMap, brdMap) {
  return {
    ...r,
    species:      specMap[r.speciesUuid] || specMap[r.speciesCode] || r.species || r.speciesCode || null,
    breedEcotype: r.breedEcotype || brdMap[r.breedUuid]   || null,
  };
}

const EMPTY_REF = {
  states: [],
  species: [],
  breeds: [],
  ageClasses: [],
  productionSystems: [],
  countMethods: [],
  dataSources: [],
  validationStatuses: [],
};

// ── What each role can do (capability matrix) ─────────────────────────────────
// Status eligibility is checked separately per action.
const ROLE_CAN = {
  R01: { edit: true, submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { edit: false, submit: false, validate: true,  approve: false, reject: true,  delete: false },
  R04: { edit: true, submit: true,  validate: true,  approve: false, reject: true,  delete: false },
  R05: { edit: true, submit: true,  validate: false, approve: false, reject: false, delete: false },
};

// Role → which record statuses that role may edit the content fields of.
// Mirrors the live backend's authoritative rule, PopulationRecordPolicy::update()
// (NlimsBackend/app/Policies/PopulationRecordPolicy.php) — the status gate
// (Draft or Rejected only) is identical for every permitted role, and only
// SystemAdministrator (R01), SubNationalValidator (R04), and DataOriginator
// (R05) are in the allowed-role list at all. R02/R03 (national data-steward/
// reviewer roles) cannot edit content under any status.
const EDITABLE_STATUSES_BY_ROLE = {
  R01: ['draft', 'rejected'],
  R02: [],
  R03: [],
  R04: ['draft', 'rejected'],
  R05: ['draft', 'rejected'],
};

function getMenuItems(row, roleId) {
  const status = (row?.data_status ?? row?.validationStatus ?? 'draft').toLowerCase();
  const rc     = ROLE_CAN[roleId] ?? {};

  const showEdit = rc.edit && (EDITABLE_STATUSES_BY_ROLE[roleId] ?? []).includes(status);

  const showSubmit = rc.submit && (
    (roleId === 'R05' && (status === 'draft' || status === 'rejected')) ||
    ((roleId === 'R04' || roleId === 'R01') && status === 'draft')
  );

  const showValidate = rc.validate && status === 'submitted';
  const showApprove  = rc.approve  && status === 'validated';
  const showReject   = rc.reject && (
    roleId === 'R02'
      ? status === 'validated'
      : ['submitted', 'validated'].includes(status)
  );
  const showDelete   = roleId === 'R01' && rc.delete && status === 'draft';

  return { showEdit, showSubmit, showValidate, showApprove, showReject, showDelete };
}

// ── Action dropdown ───────────────────────────────────────────────────────────
const STATUS_PILL_CLS = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved:  'bg-green-50 text-green-700',
  rejected:  'bg-red-50 text-red-600',
};

function ActionDropdown({ row, rect, roleId, onClose, onView, onEdit, onSubmit, onValidate, onApprove, onReject, onDelete }) {
  const { showEdit, showSubmit, showValidate, showApprove, showReject, showDelete } = getMenuItems(row, roleId);
  const status = (row?.data_status ?? row?.validationStatus ?? 'draft').toLowerCase();

  // Always opens directly below the button and follows it on scroll.
  const { menuRef, style: anchoredStyle } = useAnchoredMenu(rect, onClose);
  const style = rect ? { ...anchoredStyle, minWidth: 210 } : { display: 'none' };

  const pill = STATUS_PILL_CLS[status] ?? STATUS_PILL_CLS.draft;

  function Item({ onClick, colorCls, icon, label, dividerBefore }) {
    return (
      <>
        {dividerBefore && <div className="my-1 h-px bg-gray-100 mx-3" />}
        <button
          type="button"
          onClick={() => { onClick(); onClose(); }}
          className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-left whitespace-nowrap transition-colors ${colorCls}`}
        >
          <span className="w-4 flex justify-center flex-shrink-0">{icon}</span>
          {label}
        </button>
      </>
    );
  }

  const workflowItems = showSubmit || showValidate || showApprove;

  // Portal to <body>: a transformed/filtered ancestor (page transitions,
  // card effects) would otherwise make `position: fixed` relative to that
  // ancestor and throw the menu away from its button.
  return createPortal(
    <>
      {/* Backdrop (closes menu on outside click) */}
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />

      <div ref={menuRef} role="menu" style={style} className="rounded-xl bg-white shadow-2xl border border-gray-100 py-1.5 overflow-x-hidden">
        {/* Current status badge */}
        <div className="px-4 pb-2 pt-1 border-b border-gray-50 flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${pill}`}>{status}</span>
        </div>

        {/* Always: View Details */}
        <Item
          onClick={onView}
          colorCls="text-gray-700 hover:bg-gray-50"
          icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
          label="View Details"
        />

        {/* Edit (role + status dependent) */}
        {showEdit && (
          <Item
            onClick={onEdit}
            colorCls="text-gray-700 hover:bg-gray-50"
            icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
            label="Edit Record"
          />
        )}

        {/* Workflow actions */}
        {workflowItems && (
          <>
            <div className="my-1 h-px bg-gray-100 mx-3" />
            {showSubmit && (
              <Item
                onClick={onSubmit}
                colorCls="text-blue-700 hover:bg-blue-50"
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>}
                label="Submit for Review"
              />
            )}
            {showValidate && (
              <Item
                onClick={onValidate}
                colorCls="text-amber-700 hover:bg-amber-50"
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                label="Validate"
              />
            )}
            {showApprove && (
              <Item
                onClick={onApprove}
                colorCls="text-green-700 hover:bg-green-50"
                icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                label="Approve"
              />
            )}
          </>
        )}

        {/* Reject */}
        {showReject && (
          <Item
            onClick={onReject}
            colorCls="text-red-600 hover:bg-red-50"
            dividerBefore
            icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            label="Reject"
          />
        )}

        {/* Delete */}
        {showDelete && (
          <Item
            onClick={onDelete}
            colorCls="text-red-600 hover:bg-red-50"
            dividerBefore={!showReject}
            icon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>}
            label="Delete Draft"
          />
        )}
      </div>
    </>,
    document.body
  );
}

// ── Reject notes modal ────────────────────────────────────────────────────────
function RejectNotesModal({ onConfirm, onClose }) {
  const [notes, setNotes]   = useState('');
  const [busy,  setBusy]    = useState(false);
  const [error, setError]   = useState('');
  const textRef = useRef(null);

  useEffect(() => { textRef.current?.focus(); }, []);

  const handleConfirm = async () => {
    if (!notes.trim() || busy) return;
    setBusy(true); setError('');
    try {
      await onConfirm(notes.trim());
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Reject failed.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Reject Record</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The record will return to Draft. The rejection reason will be visible to the originating officer.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Rejection Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          ref={textRef}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={4}
          placeholder="Describe what must be corrected before the record can be resubmitted…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700
            focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400
            resize-none placeholder-gray-400"
        />
        {!notes.trim() && (
          <p className="mt-1 text-xs text-red-500">Rejection reason is required.</p>
        )}

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!notes.trim() || busy}
            className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white
              hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? 'Rejecting…' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteConfirmModal({ row, onConfirm, onClose, busy }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Draft Record?</h3>
        <p className="text-sm text-gray-600">
          You are about to permanently delete the draft record for{' '}
          <strong className="text-gray-900">{row?.state}{row?.lga ? ` — ${row.lga}` : ''}</strong>.
          This cannot be undone.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quick-action busy overlay ─────────────────────────────────────────────────
function QuickActionOverlay({ label }) {
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/20">
      <div className="rounded-xl bg-white px-6 py-4 shadow-xl text-sm font-semibold text-gray-700 flex items-center gap-3">
        <svg className="animate-spin h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {label}
      </div>
    </div>
  );
}

function populationRecordLabel(row) {
  return `${row?.state ?? ''}${row?.lga ? ` — ${row.lga}` : ''}`.trim() || row?.uuid || row?.id;
}

function getSpeciesUuid(species) {
  return species?.uuid ?? species?.id ?? species?.species_id ?? species?.speciesUuid ?? '';
}

function getSpeciesCode(species) {
  return species?.species_code ?? species?.speciesCode ?? species?.code ?? '';
}

function getSpeciesValue(species) {
  return getSpeciesUuid(species) || getSpeciesCode(species);
}

function getSpeciesLabel(species) {
  return getSpeciesCode(species)
    || species?.speciesLabel
    || species?.species_name
    || species?.name
    || species?.common_name_nigeria
    || getSpeciesValue(species);
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PopulationDataTable({ canCreate = true, onAddRecord, hideFilters = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshNotifications } = useSync();
  const { pushWorkflowNotification } = useNotifications();
  const roleId = user?.roleId ?? 'R06';

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [filters,     setFilters]     = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [refData,     setRefData]     = useState(EMPTY_REF);

  // Modal states
  const [actionMenu,   setActionMenu]   = useState(null);   // { row, rect }
  const [rejectTarget, setRejectTarget] = useState(null);   // row being rejected
  const [deleteTarget, setDeleteTarget] = useState(null);   // row being deleted
  const [quickBusy,    setQuickBusy]    = useState('');     // label while quick-action runs

  const requestRef     = useRef(0);
  const specMapRef     = useRef({});
  const brdMapRef      = useRef({});
  const speciesListRef = useRef([]);

  useEffect(() => {
    // Switching roles should not carry any stale client-side filters or modal
    // state forward. We always want to show the current backend collection.
    setFilters({});
    setSearchQuery('');
    setActionMenu(null);
    setRejectTarget(null);
    setDeleteTarget(null);
    setQuickBusy('');
  }, [roleId]);

  // ── Load reference data once ───────────────────────────────────────────────
  useEffect(() => {
    loadPopulationRefData().then((data) => {
      const species = Array.isArray(data?.species) ? data.species : [];
      const breeds = Array.isArray(data?.breeds) ? data.breeds : [];

      setRefData({
        ...data,
        species,
        breeds,
      });
      speciesListRef.current = species;
      specMapRef.current = Object.fromEntries(
        species.flatMap((s) => {
          const label = getSpeciesLabel(s);
          return [getSpeciesUuid(s), getSpeciesCode(s)]
            .filter(Boolean)
            .map((key) => [key, label]);
        })
      );
      brdMapRef.current = Object.fromEntries(
        breeds.map((b) => [b.uuid, b.breed_name || b.breed_code || b.breedName || b.breedCode])
      );
      setRows((prev) => prev.map((r) => enrichRow(r, specMapRef.current, brdMapRef.current)));
    });
  }, []);

  // ── Fetch records — one large unfiltered batch; dropdown filters are
  // applied client-side below (filteredRows), not sent to the backend. ──────
  const loadData = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true); setError('');
    try {
      const { items } = await populationRecordsApi.listAll(toApiParams());
      if (requestId !== requestRef.current) return;
      setRows(items.map((r) => enrichRow(r, specMapRef.current, brdMapRef.current)));
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to load population records.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const reloadRecords = useCallback(() => loadData(), [loadData]);
  const reloadRecordsAndNotify = useCallback(() => {
    reloadRecords();
    refreshNotifications();
  }, [reloadRecords, refreshNotifications]);
  useCollectionSync(populationRecordsApi.collectionKey, reloadRecords);

  const filteredRows = useMemo(() => filterPopulationRows(rows, filters), [rows, filters]);

  // ── Filter handler ─────────────────────────────────────────────────────────
  const handleFiltersChange = useCallback((f) => setFilters(f), []);

  // ── Row action (three-dot) ─────────────────────────────────────────────────
  // NlimsDataTable passes (action, rowData, { button, row, event }) to onRowAction
  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action === 'view') {
      setActionMenu({ row, rect });
    }
  }, []);

  // ── Quick action: Submit ───────────────────────────────────────────────────
  const handleQuickSubmit = useCallback(async (row) => {
    setQuickBusy('Submitting…');
    try {
      await populationRecordsApi.submit(row.uuid ?? row.id);
      pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: 'submitted', actorRoleId: roleId, recordLabel: populationRecordLabel(row) });
      reloadRecordsAndNotify();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Submit failed.');
    } finally {
      setQuickBusy('');
    }
  }, [reloadRecordsAndNotify, pushWorkflowNotification, roleId]);

  // ── Quick action: Validate ─────────────────────────────────────────────────
  const handleQuickValidate = useCallback(async (row) => {
    setQuickBusy('Validating…');
    try {
      await populationRecordsApi.validate(row.uuid ?? row.id);
      pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: 'validated', actorRoleId: roleId, recordLabel: populationRecordLabel(row) });
      reloadRecordsAndNotify();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Validation failed.');
    } finally {
      setQuickBusy('');
    }
  }, [reloadRecordsAndNotify, pushWorkflowNotification, roleId]);

  // ── Quick action: Approve ─────────────────────────────────────────────────
  const handleQuickApprove = useCallback(async (row) => {
    setQuickBusy('Approving…');
    try {
      await populationRecordsApi.approve(row.uuid ?? row.id);
      pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: 'approved', actorRoleId: roleId, recordLabel: populationRecordLabel(row) });
      reloadRecordsAndNotify();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Approval failed.');
    } finally {
      setQuickBusy('');
    }
  }, [reloadRecordsAndNotify, pushWorkflowNotification, roleId]);

  // ── Quick action: Reject (opens notes modal) ──────────────────────────────
  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    await populationRecordsApi.reject(rejectTarget.uuid ?? rejectTarget.id, notes);
    pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: 'rejected', actorRoleId: roleId, recordLabel: populationRecordLabel(rejectTarget), notes });
    setRejectTarget(null);
    reloadRecordsAndNotify();
  }, [rejectTarget, reloadRecordsAndNotify, pushWorkflowNotification, roleId]);

  // ── Quick action: Delete (via confirmation modal) ─────────────────────────
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy('Deleting…');
    try {
      await populationRecordsApi.remove(deleteTarget.uuid ?? deleteTarget.id);
      setDeleteTarget(null);
      reloadRecordsAndNotify();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Delete failed.');
    } finally {
      setQuickBusy('');
    }
  }, [deleteTarget, reloadRecordsAndNotify]);

  return (
    <section className="space-y-3">
      <PopulationFilterBar
        canCreate={canCreate}
        onSearch={setSearchQuery}
        onFiltersChange={handleFiltersChange}
        onAddRecord={onAddRecord ?? (() => navigate('/livestock-population/new?subModule=population'))}
        refData={refData}
        hideFilters={hideFilters}
      />

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => { setError(''); loadData(); }}
            className="ml-4 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          Loading population records…
        </div>
      )}

      <NlimsDataTable
        key={roleId}
        tableId="livestock-population-table"
        columns={POPULATION_COLUMNS}
        data={filteredRows}
        searchQuery={searchQuery}
        hideSearch
        defaultSort={[0, 'asc']}
        enableRowSelection
        pageLength={25}
        onRowAction={handleRowAction}
      />

      {/* ── Action dropdown (three-dot menu) ─────────────────────────── */}
      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/livestock-population/view/${actionMenu.row.uuid}`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/livestock-population/edit/${actionMenu.row.uuid}`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => { const r = actionMenu.row; setActionMenu(null); handleQuickSubmit(r); }}
          onValidate={() => { const r = actionMenu.row; setActionMenu(null); handleQuickValidate(r); }}
          onApprove={() => { const r = actionMenu.row; setActionMenu(null); handleQuickApprove(r); }}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {/* ── Reject notes modal ───────────────────────────────────────── */}
      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {/* ── Delete confirmation modal ────────────────────────────────── */}
      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={!!quickBusy}
        />
      )}

      {/* ── Quick-action busy overlay ────────────────────────────────── */}
      {quickBusy && <QuickActionOverlay label={quickBusy} />}
    </section>
  );
}
